import { SliceProps } from "@/Slice/index"
import { CTA, GRichText } from "@/components"
import { Link, NextImage, cn } from "@jog/react-components"
import { asImageSrc, asLink, asText, isFilled } from "@prismicio/helpers"
import { CSSProperties } from "react"

interface SingleBannerButtonsProperties extends CSSProperties {
    ["--lg-top"]: `${number}%`
    ["--lg-left"]: `${number}%`
    ["--top"]: `${number}%`
}
export const SingleBanner = ({
    slice: { primary, items },
    className,
    lcp,
}: SliceProps<"single_banner"> & { lcp: boolean }) => {
    const showButtons =
        (!!primary.buttons_top_position && !!primary.buttons_left_position) ||
        (!!primary.buttons_top_position_mobile && !!primary.buttons_left_position_mobile)
    const style: SingleBannerButtonsProperties = {
        "--lg-top": `${primary.buttons_top_position || 0}%`,
        "--lg-left": `${primary.buttons_left_position || 0}%`,
        "--top": `${primary.buttons_top_position_mobile || 0}%`,
    }

    return (
        <div className={className}>
            <div className="relative h-full bg-contain bg-center bg-no-repeat text-center">
                {primary.image_desktop && (
                    <NextImage
                        className="hidden h-auto w-full max-w-full bg-transparent lg:inline"
                        src={asImageSrc(primary.image_desktop) || ""}
                        width={primary.image_desktop.dimensions?.width}
                        height={primary.image_desktop.dimensions?.height}
                        sizes="100vw"
                        alt={primary.image_desktop.alt || "Single Banner Image Desktop"}
                        lcp={lcp}
                    />
                )}
                {primary.image_mobile && (
                    <NextImage
                        className="inline h-auto w-full max-w-full bg-transparent lg:hidden"
                        src={asImageSrc(primary.image_mobile) || ""}
                        width={primary.image_mobile.dimensions?.width}
                        height={primary.image_mobile.dimensions?.height}
                        sizes="100vw"
                        alt={primary.image_mobile.alt || "Single Banner Image Mobile"}
                        lcp={lcp}
                    />
                )}

                {isFilled.richText(primary.text) && (
                    <>
                        <div
                            className={cn(
                                "absolute left-1/2 flex w-[80%] -translate-x-1/2 -translate-y-1/2 flex-col lg:hidden",
                                {
                                    "items-center text-center":
                                        primary.text_alignment === "center" || !primary.text_alignment,
                                    "items-start text-left": primary.text_alignment === "left",
                                    "items-end text-right": primary.text_alignment === "right",
                                }
                            )}
                            style={{
                                color: primary.text_color || undefined,
                                top: `${primary.text_top_position_mobile || 50}%`,
                            }}>
                            <GRichText field={primary.text} />
                        </div>
                        <div
                            className={cn(
                                "absolute left-1/2 hidden w-[80%] -translate-x-1/2 -translate-y-1/2 lg:flex lg:flex-col",
                                {
                                    "items-center text-center":
                                        primary.text_alignment === "center" || !primary.text_alignment,
                                    "items-start text-left": primary.text_alignment === "left",
                                    "items-end text-right": primary.text_alignment === "right",
                                }
                            )}
                            style={{
                                color: primary.text_color || undefined,
                                top: `${primary.text_top_position || 50}%`,
                            }}>
                            <GRichText field={primary.text} />
                        </div>
                    </>
                )}

                {showButtons && (
                    <div
                        style={style}
                        className="absolute top-[var(--top)] left-0 right-0 z-[2] -translate-y-1/2 px-6 lg:right-auto lg:top-[var(--lg-top)] lg:left-[var(--lg-left)] lg:-translate-x-1/2 lg:px-0">
                        <div className="-mx-1 flex flex-wrap items-center justify-center">
                            {items
                                .filter((item) => asText(item.button_label))
                                .map((item, index) => {
                                    if (!isFilled.link(item.button_url)) return null
                                    return (
                                        <Link
                                            href={asLink(item.button_url)}
                                            key={index}
                                            className={cn(
                                                "w-1/2 items-center justify-center overflow-hidden p-1 lg:w-auto",
                                                item.is_hide_on_mobile ? "hidden lg:flex" : "flex"
                                            )}
                                            target={
                                                item.button_url.link_type === "Web" ? item.button_url.target : "_self"
                                            }>
                                            <CTA
                                                className="w-full lg:w-auto"
                                                color={primary.button_text_color || ""}
                                                bgColor={primary.buttons_bg_color || ""}
                                                colorHover={primary.buttons_hover_text_color || ""}
                                                bgColorHover={primary.buttons_hover_bg_color || ""}
                                                borderWidth={primary.buttons_border_width || 0}
                                                borderColor={primary.buttons_border_color || ""}
                                                fontSizeMobile={primary.buttons_font_size_mobile || ""}>
                                                {asText(item.button_label)}
                                            </CTA>
                                        </Link>
                                    )
                                })}
                        </div>
                    </div>
                )}
                {isFilled.link(primary.banner_url) ? (
                    <Link
                        href={asLink(primary.banner_url)}
                        className="absolute inset-0 z-[1] cursor-pointer"
                        target={primary.banner_url.link_type === "Web" ? primary.banner_url.target : "_self"}
                        aria-label={primary.image_desktop.alt || primary.image_mobile.alt || "Banner"}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
